import { reactive, ref } from 'vue';
import type { DebitNormsWithOrder } from '../types';

type DebitNormsDispatchAction =
  | { type: 'reset'; entries: DebitNormsWithOrder[] }
  | { type: 'start-edit' }
  | { type: 'request-save' }
  | { type: 'complete-save' }
  | { type: 'revert-save' }
  | { type: 'cancel' }
  | { type: 'add-entry'; entry: DebitNormsWithOrder }
  | { type: 'delete-entry'; id: string }
  | { type: 'update-entry'; id: string; rate: number }
  | {
      type: 'update-new-entry';
      field: 'accountNumber' | 'rate';
      value: string;
    };

export const createDebitNormsStore = (
  initialEntries: DebitNormsWithOrder[],
) => {
  const entries = reactive<DebitNormsWithOrder[]>(
    initialEntries.map((e) => ({ ...e, account: { ...e.account } })),
  );
  const status = ref<'idle' | 'editing' | 'saving'>('idle');
  const newEntry = reactive<{ accountNumber: string; rate: string }>({
    accountNumber: '',
    rate: '',
  });
  let snapshot: DebitNormsWithOrder[] | null = null;

  const dispatch = (
    action: DebitNormsDispatchAction,
  ): DebitNormsWithOrder[] | null => {
    switch (action.type) {
      case 'reset': {
        entries.splice(
          0,
          entries.length,
          ...action.entries.map((e) => ({ ...e, account: { ...e.account } })),
        );
        status.value = 'idle';
        newEntry.accountNumber = '';
        newEntry.rate = '';
        return null;
      }

      case 'start-edit': {
        snapshot = entries.map((e) => ({ ...e, account: { ...e.account } }));
        status.value = 'editing';
        return null;
      }

      case 'request-save': {
        status.value = 'saving';
        return entries.map((e) => ({ ...e, account: { ...e.account } }));
      }

      case 'complete-save': {
        snapshot = null;
        status.value = 'idle';
        return null;
      }

      case 'revert-save': {
        status.value = 'editing';
        return null;
      }

      case 'cancel': {
        if (snapshot) {
          entries.splice(0, entries.length, ...snapshot);
        }
        snapshot = null;
        newEntry.accountNumber = '';
        newEntry.rate = '';
        status.value = 'idle';
        return null;
      }

      case 'add-entry': {
        entries.push(action.entry);
        newEntry.accountNumber = '';
        newEntry.rate = '';
        return null;
      }

      case 'delete-entry': {
        const idx = entries.findIndex((e) => e.id === action.id);
        if (idx !== -1) entries.splice(idx, 1);
        return null;
      }

      case 'update-entry': {
        const entry = entries.find((e) => e.id === action.id);
        if (entry && action.rate > 0) {
          entry.rate = action.rate;
        }
        return null;
      }

      case 'update-new-entry': {
        newEntry[action.field] = action.value;
        return null;
      }
    }
  };

  return { entries, status, newEntry, dispatch };
};
